import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import screenService from '@/utils/services/ScreenService.js';


const CreateImageModal = ({ uid, onClose, onCreated }) => {
  const [screenName, setScreenName] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [angle, setAngle] = useState(0);

  // Load pre-filled data from sessionStorage
  useEffect(() => {
    const prefillScreenName = sessionStorage.getItem('prefillScreenName')
    const prefillImageUrl = sessionStorage.getItem('prefillImageUrl')
    
    if (prefillScreenName) {
      setScreenName(prefillScreenName)
      sessionStorage.removeItem('prefillScreenName')
    }
    
    if (prefillImageUrl) {
      setImagePreview(prefillImageUrl)
      sessionStorage.removeItem('prefillImageUrl')
    }
  }, [])


  // Prevent background scroll when modal is open
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  // Get aspect ratio based on angle
  const getImageAspectRatio = (angle) => {
    switch(angle) {
      case 0:
      case 180:
        return "16/9";
      case 90:
      case 270:
        return "9/16";
      default:
        return "16/9";
    }
  };

  // Rotate left (counter-clockwise)
  const rotateLeft = () => {
    setAngle(prevAngle => {
      const newAngle = (prevAngle + 90) % 360;
      return newAngle;
    });
  };

  // Rotate right (clockwise)
  const rotateRight = () => {
    setAngle(prevAngle => {
      const newAngle = (prevAngle - 90 + 360) % 360;
      return newAngle;
    });
  };

  // Handle image selection
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };



  // Convert file to base64 data URL
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  // Handle form submit
  const handleCreate = async () => {
    setError('');
    if (!screenName.trim()) {
      setError('Please provide the screen name');
      return;
    }
    if (!imageFile) {
      setError('Please select an image.');
      return;
    }
    if (!uid) {
      setError('User not authenticated. Please log in again.');
      return;
    }
    
    setLoading(true);
    try {
      // Convert image to base64
      const imageDataUrl = await fileToBase64(imageFile);
      
      // Prepare screen data
      const screenData = {
        name: screenName.trim(),
        angle: angle
      };
      
      // Use ScreenService to create the screen
      await screenService.createScreen(screenData, imageDataUrl, uid);
      setLoading(false);
      if (onCreated) onCreated();
      onClose();
    } catch (err) {
      console.error('Error creating screen:', err);
      setError('Failed to create screen. ' + (err.message || ''));
      setLoading(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] bg-black bg-opacity-60 overflow-hidden touch-none pointer-events-auto">
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl border border-gray-200 shadow-2xl w-full max-w-2xl overflow-hidden overflow-y-auto max-h-screen">
        {/* Header */}
        <div className="flex items-center justify-between px-10 py-6 border-b border-gray-100">
          <span className="font-bold text-2xl text-gray-900">Create DigiScreen</span>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors">
            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        {/* Content */}
        <div className="px-12 py-8 bg-gray-50">
          <label className="block mb-3 font-medium text-gray-700 text-lg">Screen Name</label>
          <input
            className="w-full border border-gray-200 rounded-lg px-6 py-4 mb-2 text-xl focus:outline-none focus:ring-2 focus:ring-yellow-400"
            placeholder="Enter the screen name"
            value={screenName}
            onChange={e => setScreenName(e.target.value)}
            disabled={loading}
            maxLength={64}
          />
          <p className="text-sm text-gray-500 mb-6">Give your screen a descriptive name to easily identify it later</p>

          <div className="mb-6">
            <label className="block mb-3 font-medium text-gray-700 text-lg">Image</label>
            {imagePreview ? (
              <div className="mb-6 flex flex-col items-center">
                <div className="relative mb-4">
                  {(angle === 0 || angle === 180 || !angle) ? (
                    <>
                      {/* Landscape TV Frame (0°, 180°, or no angle) */}
                      <div 
                        className="relative border-2 border-black inline-block p-1 m-1 overflow-hidden transition-transform duration-300 bg-gradient-to-br from-yellow-50 to-yellow-100 shadow-2xl rounded-lg"
                        style={{
                          borderBottomWidth: (angle === 0 || !angle) ? '20px' : '2px',
                          borderRightWidth: '2px',
                          borderTopWidth: angle === 180 ? '20px' : '2px',
                          borderLeftWidth: '2px',
                          width: '400px',
                          height: '267px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.1), 0 0 20px rgba(255, 193, 7, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
                          background: 'linear-gradient(135deg, #fefce8 0%, #fef3c7 50%, #fde68a 100%)',
                          borderColor: '#1f2937',
                          borderStyle: 'solid'
                        }}
                      >
                        <div className="overflow-hidden flex items-center justify-center w-full h-full rounded-md bg-gradient-to-br from-gray-900 to-gray-800 p-1 relative">
                          <img 
                            src={imagePreview} 
                            alt="Preview" 
                            className="w-full h-full object-contain rounded-lg"
                            style={{
                              transition: 'transform 0.3s ease',
                              filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3))'
                            }}
                          />
                        </div>
                      </div>
                      {/* TV Power Button - Separate Element */}
                      {(angle === 0 || angle === 180 || !angle) && (
                        <div 
                          className="absolute z-30"
                          style={{
                            bottom: (angle === 0 || !angle) ? '4px' : 'auto',
                            top: angle === 180 ? '4px' : 'auto',
                            left: '50%',
                            transform: 'translateX(-50%)'
                          }}
                        >
                          <div className="w-4 h-4 bg-gradient-to-br from-gray-500 to-gray-600 rounded-full border border-gray-400 shadow-md flex items-center justify-center">
                            <div className="w-1 h-1 bg-red-400 rounded-full shadow-inner"></div>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      {/* Portrait TV Frame (90° and 270°) */}
                      <div 
                        className="relative border-2 border-black inline-block p-1 m-1 overflow-hidden transition-transform duration-300 bg-gradient-to-br from-yellow-50 to-yellow-100 shadow-2xl rounded-lg"
                        style={{
                          borderBottomWidth: '2px',
                          borderRightWidth: angle === 270 ? '20px' : '2px',
                          borderTopWidth: '2px',
                          borderLeftWidth: angle === 90 ? '20px' : '2px',
                          width: '267px',
                          height: '400px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.1), 0 0 20px rgba(255, 193, 7, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
                          background: 'linear-gradient(135deg, #fefce8 0%, #fef3c7 50%, #fde68a 100%)',
                          borderColor: '#1f2937',
                          borderStyle: 'solid'
                        }}
                      >
                        <div className="overflow-hidden flex items-center justify-center w-full h-full rounded-md bg-gradient-to-br from-gray-900 to-gray-800 p-1 relative">
                          <img 
                            src={imagePreview} 
                            alt="Preview" 
                            className="w-full h-full object-contain rounded-lg"
                            style={{
                              transition: 'transform 0.3s ease',
                              filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3))'
                            }}
                          />
                        </div>
                      </div>
                    </>
                  )}
                </div>
                <button
                  className="px-4 py-2 bg-gray-200 rounded-lg text-gray-700 hover:bg-gray-300 text-base"
                  onClick={() => { setImageFile(null); setImagePreview(null); }}
                  disabled={loading}
                >Remove</button>
              </div>
            ) : null}
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              disabled={loading}
              className="block w-full text-base text-gray-500 file:mr-4 file:py-3 file:px-6 file:rounded-lg file:border-0 file:text-base file:font-semibold file:bg-yellow-50 file:text-yellow-700 hover:file:bg-yellow-100"
            />
            <p className="text-sm text-gray-500 mt-2">💡 For best results, use images with 1920x1080 resolution (16:9 aspect ratio)</p>
          </div>
          {/* Rotation Controls */}
          {imagePreview && (
            <div className="mb-6">
              <label className="block mb-3 font-medium text-gray-700 text-lg">TV Orientation</label>
              <div className="flex justify-center">
                <div className="btn-group">
                  <button
                    type="button"
                    className="px-4 py-2 bg-blue-600 text-white rounded-l-lg hover:bg-blue-700 transition-colors text-base font-medium"
                    onClick={rotateLeft}
                    disabled={loading}
                    title="Rotate Left"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    className="px-4 py-2 bg-blue-600 text-white rounded-r-lg hover:bg-blue-700 transition-colors text-base font-medium"
                    onClick={rotateRight}
                    disabled={loading}
                    title="Rotate Right"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </button>
                </div>
              </div>
              <p className="text-sm text-gray-500 mt-2 text-center">🔄 Use the rotation arrows to adjust the TV orientation for your display setup</p>
            </div>
          )}
          {error && <div className="text-red-500 mb-3 text-base">{error}</div>}
        </div>
        {/* Actions */}
        <div className="flex justify-end gap-3 px-12 py-6 bg-gray-50 border-t border-gray-100">
          <button
            className="px-5 py-3 rounded-lg text-lg text-gray-700 hover:bg-gray-100 transition-colors"
            onClick={onClose}
            disabled={loading}
          >Close</button>
          <button
            className="px-5 py-3 rounded-lg bg-yellow-500 text-white text-lg font-semibold hover:bg-yellow-600 transition-colors disabled:opacity-60"
            onClick={handleCreate}
            disabled={loading}
          >{loading ? 'Creating...' : 'Create DigiScreen'}</button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CreateImageModal; 